<template>
  <div class="register-tab">
    <SalesRegisterToolbar
        ref="toolbarRef"
        :filters="appliedFilters"
        :chip-meta="filterChipMeta"
        @go="onClickGo"
        @open-filter="onOpenFilter"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @remove-chip="onRemoveChip"
        @print="onClickToPrint"
        @export="exportTable"
    >
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product/Service Type</label>
        <v-select
            placeholder="Select Product/Service Type"
            v-model="draftFilters.product_type"
            :options="productTypes"
            label="text"
            @update:modelValue="onChangeProductType"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product Group</label>
        <AsyncSelect
            placeholder="Select Product Group"
            :api-service="fetchProductList"
            v-model="draftFilters.product_group"
            label="text"
            :additional-query="groupQueryObj"
            :format-label="option => option.text"
            :option-selected="onChangeProductGroup"
            ref="groupSelector"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Product/Service</label>
        <AsyncSelect
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="draftFilters.product"
            label="text"
            :additional-query="productQueryObj"
            :format-label="option => option.text"
            ref="productSelector"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Project</label>
        <v-select
            placeholder="Select Project"
            v-model="draftFilters.project"
            :options="projectList"
            label="name"
        />
      </div>
      <div class="col-12 col-sm-6">
        <div class="d-flex align-items-center justify-content-between">
          <label class="filter-label">Supplier</label>
          <span class="filter-search-icon" @click="onOpenContactSearchModal">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <AsyncSelect
            placeholder="Supplier Name, ID, Mobile No"
            v-model="draftFilters.supplier"
            :api-service="fetchContactProfiles"
            :additional-query="supplierQueryObj"
            :format-label="formatPatientLabel"
        />
      </div>
    </SalesRegisterToolbar>
    <RegisterTable
        tab="item"
        :rows="rows"
        :current-page="pageData.current_page"
        :last-page="pageData.last_page"
        :per-page="pageData.per_page"
        :total-value="registerData.total"
        :is-loaded="hasLoaded"
    />
    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>
    <ContactDropdownFilter
        v-if="$store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="supplier"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import SalesRegisterToolbar from '@/components/molecule/company/inventory/product-sales-register/SalesRegisterToolbar.vue'
import RegisterTable from '@/components/molecule/company/inventory/purchase-register/RegisterTable.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import usePurchaseRegisterTab from '@/composables/usePurchaseRegisterTab'

const props = defineProps({
  projectList: {
    type: Array,
    default: () => []
  },
  homeData: {
    type: Object,
    default: () => ({})
  }
})

const { fetchProductList } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()

const productSelector = ref(null)
const groupSelector = ref(null)
const supplierQueryObj = { roles: 'supplier' }

const filterChipMeta = [
  { key: 'product_type', label: 'Product Type', text_key: 'text' },
  { key: 'product_group', label: 'Product Group', text_key: 'text' },
  { key: 'product', label: 'Product', text_key: 'text' },
  { key: 'project', label: 'Project', text_key: 'name' },
  { key: 'supplier', label: 'Supplier', text_key: 'name' }
]

const getEmptyFilters = () => {
  return {
    product_type: null,
    product_group: null,
    product: null,
    project: null,
    supplier: null
  }
}

const getFilterQuery = (filters) => {
  let query = ''
  if (filters.product_type) query += '&product_type=' + filters.product_type.id
  if (filters.product_group) query += '&ancestor_id=' + filters.product_group.id
  if (filters.product) query += '&product_id=' + filters.product.id
  if (filters.project) query += '&project_id=' + filters.project.id
  if (filters.supplier) query += '&contact_profile_id=' + filters.supplier.id
  return query
}

const {
  offset,
  loading,
  hasLoaded,
  rows,
  registerData,
  pageData,
  toolbarRef,
  appliedFilters,
  draftFilters,
  onClickGo,
  onPageChange,
  onOpenFilter,
  onApplyFilters,
  onClearFilters,
  onRemoveChip,
  onOpenContactSearchModal,
  onSearchContact,
  onClickToPrint,
  exportTable
} = usePurchaseRegisterTab('item', getEmptyFilters, getFilterQuery)

const productTypes = computed(() => {
  const types = props.homeData.product_type
  if (! types) return []
  return Object.keys(types).map(key => {
    return { id: key, text: types[key] }
  })
})

const productQueryObj = computed(() => {
  const filters = draftFilters.value
  const queryObj = {}
  if (filters.product_type) queryObj.product_type = filters.product_type.id
  if (filters.product_group) queryObj.ancestor_id = filters.product_group.id
  return queryObj
})

const groupQueryObj = computed(() => {
  const queryObj = {
    group_level: 1
  }
  if (draftFilters.value.product_type) queryObj.product_type = draftFilters.value.product_type.id
  return queryObj
})

const clearSelector = (selector) => {
  if (! selector.value) return
  selector.value.clear()
}

const onChangeProductType = () => {
  draftFilters.value.product_group = null
  draftFilters.value.product = null
  clearSelector(groupSelector)
  clearSelector(productSelector)
}

const onChangeProductGroup = () => {
  draftFilters.value.product = null
  clearSelector(productSelector)
}
</script>
